import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { PlanType } from "api-types/modelTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { assertValidatesSchemaTODO } from "doc/schema/docSchemaAssertion";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import {
  HomePlanDropdownModalState,
  HomePlanDropdownModals,
} from "lib/components/builders/communities/plans/homePlans/HomePlanDropdown";
import HomePlansTable from "lib/components/builders/communities/plans/homePlans/HomePlansTable";
import EditHomePlanModal from "lib/components/builders/communities/plans/modals/CreateOrUpdateHomePlanModal";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import { usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import { getLocationPlanHomePlansURL, masterPlanHomePlansURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { configurePublicPlanPath, useAppRouterParams } from "lib/routeHelpers";
import { isCreateHomePlanModalPermitted } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { useState } from "react";
import NewEntityButton from "ui/buttons/NewEntityButton/NewEntityButton";
import Loading from "ui/components/Loading";
import { useAppNavigate } from "ui/hooks/useAppNavigate";

export default function PlanLibraryHomePlansTab({ planType }: { planType: PlanType }) {
  const navigate = useAppNavigate();
  const { builderId, masterPlanId, locationId, locationPlanId } = useAppRouterParams();

  const { plans, isReadOnly, planName } = usePlanLibrarySelector((state) => ({
    plans: state.plans,
    isReadOnly: state.isReadOnly,
    planName: state.planName,
  }));

  const [activeHomePlan, setActiveHomePlan] = useState<ClientHomePlanHydratedNoDoc | null>(null);
  const [highlightedHomePlanId, setHighlightedHomePlanId] = useState<string | null>(null);
  const userContext = useUserContext();
  const [openModal, setOpenModal] = useState(false);
  const modalState = HomePlanDropdownModalState();

  const { url, fetchDeps } = (() => {
    switch (planType) {
      case "master_plan":
        return {
          fetchDeps: [builderId, masterPlanId],
          url: masterPlanHomePlansURL(builderId ?? "", masterPlanId ?? ""),
        };
      case "community_plan":
      case "lot":
        assertIsNotUndefined(builderId);
        assertIsNotUndefined(locationId);
        assertIsNotUndefined(locationPlanId);
        return {
          fetchDeps: [builderId, locationId, locationPlanId],
          url: getLocationPlanHomePlansURL({ builderId, locationId, locationPlanId }),
        };
      default:
        return {
          fetchDeps: undefined,
          url: "",
        };
    }
  })();

  const { data, mutate, loading } = useFetch<ClientHomePlanHydratedNoDoc[]>(url, fetchDeps);

  function onMultiUpdate(homePlans: ClientHomePlanHydratedNoDoc[]) {
    const updatedHomePlans: Table<ClientHomePlanHydratedNoDoc> = homePlans.reduce((output, hp) => {
      output[hp.id] = hp;
      return output;
    }, {});
    void mutate((homePlans) => {
      assertIsNotUndefined(homePlans);
      return homePlans.map((hp) => (updatedHomePlans[hp.id] ? updatedHomePlans[hp.id] : hp));
    });
  }

  function onUpdate(homePlan: ClientHomePlanHydratedNoDoc | ClientHomePlan) {
    assertValidatesSchemaTODO<ClientHomePlanHydratedNoDoc>(homePlan);
    assertIsNotNullOrUndefined(data);
    void mutate(data.map((hp) => (hp.id === homePlan.id ? homePlan : hp)));
    setHighlightedHomePlanId(homePlan.id);

    setTimeout(() => {
      setHighlightedHomePlanId(null);
    }, 1500);
  }

  function onDelete(id: string) {
    assertIsNotNullOrUndefined(data);
    void mutate(data.filter((hp) => hp.id !== id));
  }

  if (loading) {
    return <Loading />;
  }

  assertIsNotNullOrUndefined(data);

  const homePlans = plans.planType === "lot" ? data.filter((x) => x.lot?.lotNumber === plans.lot.lotNumber) : data;

  return (
    <>
      {homePlans?.length > 0 && (
        <div>
          <SectionHeader
            title="Home Plans"
            subtitle={`${isReadOnly ? "View" : "Manage"} the home plans associated with ${planName}`}
            hr
          />
          <HomePlansTable
            context="COMMUNITY_PLAN"
            homePlans={homePlans}
            menuModalState={modalState}
            highlightedRowId={highlightedHomePlanId ?? undefined}
            setActiveHomePlan={setActiveHomePlan}
            onMultiUpdate={onMultiUpdate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            location={plans.planType === "master_plan" ? undefined : plans.location}
          />
        </div>
      )}
      {!homePlans?.length && (
        <div style={{ marginTop: "30px" }}>
          <BuilderPortalEmptyCTA>
            <div className="cta-container">
              <div className="cta-title">No Home Plans</div>
              <div className="cta-text">
                Your home plans are option-specific plan sets in Higharc. You can create new home plans for different
                communities, specification levels, or customer types.{" "}
              </div>
              <NewEntityButton
                entityName="Home Plan"
                onClick={() => {
                  void (plans.planType === "master_plan"
                    ? navigate({
                        to: configurePublicPlanPath("master_plans", ensureDefined(builderId), plans.masterPlan.id),
                      })
                    : setOpenModal(true));
                }}
                disabled={!isCreateHomePlanModalPermitted(userContext)}
                disabledTooltipText="You do not have permission to create a new Home Plan."
              />
            </div>
          </BuilderPortalEmptyCTA>
        </div>
      )}
      {builderId && plans.planType !== "master_plan" ? (
        <>
          {openModal && (
            <EditHomePlanModal
              builderId={builderId}
              locationId={locationId}
              locationPlan={plans.locationPlan}
              initialLotId={plans.planType === "lot" ? plans.lot.id : undefined}
              onClose={() => setOpenModal(false)}
            />
          )}
          <HomePlanDropdownModals
            builderId={builderId}
            locationId={locationId}
            homePlan={activeHomePlan}
            communityPlan={plans.locationPlan}
            modalState={modalState}
            onSuccess={onUpdate}
          />
        </>
      ) : null}
    </>
  );
}
